import {
  AbstractMesh,
  AnimationGroup,
  Color4,
  CubeTexture,
  IParticleSystem,
  ISceneLoaderProgressEvent,
  Mesh,
  PointsCloudSystem,
  Scene,
  SceneLoader,
  SceneLoaderSuccessCallback,
  Skeleton,
  Sound,
  Texture,
  Vector3,
  VideoTexture,
  VideoTextureSettings,
  TransformNode,
} from "babylonjs";
import "babylonjs-loaders";
import { parse } from "@loaders.gl/core";
import { PLYLoader } from "@loaders.gl/ply";
import Utils from "../utils/utils";

//----------------------------------------------------------------
export interface IAssetBase {
  type: "model" | "pointcloud" | "texture" | "cubetexture" | "video" | "audio";
  path: string;
  id?: string;
  loaded?: boolean;
  loading?: boolean;
  loadProgress?: number;
  abortCtrl?: AbortController;
}

export interface IAssetPointCloud extends IAssetBase {
  size?: number;
  color?: [number, number, number];
  pcs?: PointsCloudSystem;
  mesh?: Mesh;
}

export interface IAssetModel extends IAssetBase {
  meshes?: AbstractMesh[];
  meshRoot?: AbstractMesh;
  animations?: AnimationGroup[];
  skeletons?: Skeleton[];
}

export interface IAssetTexture extends IAssetBase {
  texture?: Texture;
}

export interface IAssetCubeTexture extends IAssetBase {
  cubeTexture?: CubeTexture;
}

export interface IAssetVideo extends IAssetBase {
  videoTexture?: VideoTexture;
  videoElement?: HTMLVideoElement;
}

export interface IAssetAudio extends IAssetBase {
  audio?: Sound;
}

//----------------------------------------------------------------
export async function loadModel(asset: IAssetModel, scene: Scene, rootNode: TransformNode): Promise<void> {
  try {
    let file: File | undefined;
    const useFetch = false; // use fetch to load the model.
    if (useFetch) {
      // loading a model using fetch casuses a memory leak.
      // we came across this issue in babylonjs 7.45.0 and 7.50.0
      asset.abortCtrl = new AbortController();
      const { signal } = asset.abortCtrl;
      const response = await fetch(asset.path, { signal });
      const blob = await response.blob();
      const fileName = `model-${Utils.UUID()}.glb`; // needs a unique name to avoid caching issues.
      file = new File([blob], fileName, { type: "model/gltf-binary" });
    }
    return new Promise<void>((resolve, reject) => {
      const onSuccess: SceneLoaderSuccessCallback = (
        meshes: AbstractMesh[],
        _particleSystems: IParticleSystem[],
        skeletons: Skeleton[],
        animationGroups: AnimationGroup[]
      ) => {
        meshes.forEach(mesh => {
          if (!mesh.parent) {
            mesh.parent = rootNode;
            asset.meshRoot = mesh;
          }
        });
        animationGroups.forEach(animGroup => {
          animGroup.stop(); // stop from auto-playing.
        });
        asset.meshes = meshes;
        asset.animations = animationGroups;
        asset.skeletons = skeletons;
        resolve();
      };
      const onProgress = (_event: ISceneLoaderProgressEvent) => {
        //console.log("app.content.loadModel.progress: " + event.loaded);
      };
      const onError = (_scene: Scene, message: string, _exception?: any) => {
        console.log("app.content.loadModel.error: " + message);
        reject(new Error(message));
      };
      if (file) {
        SceneLoader.ImportMesh("", "", file, scene, onSuccess, onProgress, onError);
      } else {
        SceneLoader.ImportMesh("", asset.path, undefined, scene, onSuccess, onProgress, onError);
      }
    });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      console.log("app.content.loadModel.abort: ", asset.id);
      return;
    }
    console.error("app.content.loadModel.error: ", error);
    throw error;
  } finally {
    asset.abortCtrl = undefined;
  }
}

export async function loadPointCloud(asset: IAssetPointCloud, scene: Scene, rootNode: TransformNode): Promise<void> {
  asset.abortCtrl = new AbortController();
  const { signal } = asset.abortCtrl;
  try {
    const response = await fetch(asset.path, { signal });
    const data = await parse(response, PLYLoader);
    const pos = data.attributes.POSITION.value;
    const col = data.attributes.COLOR_0.value;
    const num = pos.length / 3;

    const pointCloudSize = asset.size ?? 1;
    const pcs = new PointsCloudSystem("pcs", pointCloudSize, scene);
    pcs.addPoints(num, (particle: { position: Vector3; color: Color4 }, i: number) => {
      const j = 3 * i;
      particle.position = new Vector3(pos[j + 0], pos[j + 1], pos[j + 2]);
      particle.color = new Color4(
        asset.color ? asset.color[0] : col[j + 0] / 255.0,
        asset.color ? asset.color[1] : col[j + 1] / 255.0,
        asset.color ? asset.color[2] : col[j + 2] / 255.0,
        1.0
      );
    });
    const mesh = await pcs.buildMeshAsync();
    mesh.parent = rootNode;
    asset.pcs = pcs;
    asset.mesh = mesh;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      console.log("app.content.loadPointCloud.abort: ", asset.id);
      return;
    }
    console.error("app.content.loadPointCloud.error: ", error);
    throw error;
  } finally {
    asset.abortCtrl = undefined;
  }
}

export async function loadTexture(asset: IAssetTexture, scene: Scene): Promise<void> {
  return new Promise((resolve, reject) => {
    const onLoad = () => {
      asset.texture = texture;
      resolve();
    };
    const onError = (message?: string, _exception?: any) => {
      reject(new Error(`Failed to load texture: ${message}`));
    };
    const texture = new Texture(asset.path, scene, undefined, false, undefined, onLoad, onError);
  });
}

export async function loadCubeTexture(asset: IAssetCubeTexture, scene: Scene): Promise<void> {
  return new Promise((resolve, reject) => {
    const onLoad = () => {
      asset.cubeTexture = cubeTexture;
      resolve();
    };
    const onError = (message?: string, _exception?: any) => {
      reject(new Error(`Failed to load texture: ${message}`));
    };
    const cubeTexture = new CubeTexture(asset.path, scene, undefined, false, undefined, onLoad, onError);
  });
}

export async function loadVideo(asset: IAssetVideo, scene: Scene): Promise<void> {
  return new Promise((resolve, reject) => {
    const addEventListeners = (videoTexture: VideoTexture) => {
      videoTexture.video.addEventListener("loadedmetadata", onLoad, { once: true }); // iOS fix.
      videoTexture.video.addEventListener("canplay", onLoad, { once: true });
      videoTexture.video.addEventListener("error", onError, { once: true });
    };
    const removeEventListeners = (videoTexture: VideoTexture) => {
      videoTexture.video.removeEventListener("loadedmetadata", onLoad);
      videoTexture.video.removeEventListener("canplay", onLoad);
      videoTexture.video.removeEventListener("error", onError);
    };
    const onLoad = (_event: Event) => {
      removeEventListeners(videoTexture);
      asset.videoTexture = videoTexture;
      videoTexture.video.pause();
      resolve();
    };
    const onError = (_e?: any) => {
      removeEventListeners(videoTexture);
      reject(new Error(`Failed to load video: ${_e}`));
    };
    const settings: VideoTextureSettings = {
      autoPlay: true,
      autoUpdateTexture: true,
    };
    let videoSrc: string | HTMLVideoElement = asset.path;
    if (asset.videoElement) {
      // when a video element is provided, use it as the source.
      // this allows for one video element to be used for multiple video textures,
      // it can also be reused to load and play different videos over the app lifecycle.
      // this is useful when video elements need to be initialised on user interaction and then can be reused.
      videoSrc = asset.videoElement;
      videoSrc.src = asset.path;
    }
    const videoTexture = new VideoTexture(
      "video", // name
      videoSrc, // src
      scene, // scene
      undefined, // generateMipMaps
      undefined, // invertY
      undefined, // samplingMode
      settings, // settings
      onError, // onError
      undefined // format
    );
    addEventListeners(videoTexture);
  });
}

export async function loadAudio(asset: IAssetAudio, scene: Scene): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const onLoad = () => {
        asset.audio = audio;
        resolve();
      };
      const audio = new Sound("audio", asset.path, scene, onLoad, {
        loop: false,
        autoplay: false,
        volume: 1,
      });
    } catch (e) {
      reject(new Error(`Failed to load audio: ${e}`));
    }
  });
}

//----------------------------------------------------------------
function unloadBase(asset: IAssetBase) {
  if (asset.abortCtrl) {
    asset.abortCtrl.abort();
    asset.abortCtrl = undefined;
  }
}

export function unloadModel(asset: IAssetModel) {
  unloadBase(asset);
  if (asset.meshes) {
    asset.meshes.forEach(mesh => {
      mesh.dispose(false, true);
    });
  }
  if (asset.animations) {
    asset.animations.forEach(animGroup => {
      animGroup.stop();
      animGroup.dispose();
    });
  }
  if (asset.skeletons) {
    asset.skeletons.forEach(skeleton => {
      skeleton.dispose();
    });
  }
  asset.meshes = undefined;
  asset.meshRoot = undefined;
  asset.animations = undefined;
  asset.skeletons = undefined;
}

export function unloadPointCloud(asset: IAssetPointCloud) {
  unloadBase(asset);
  if (asset.pcs) {
    asset.pcs.dispose();
  }
  if (asset.mesh) {
    asset.mesh.dispose(false, true);
  }
  asset.mesh = undefined;
}

export function unloadTexture(asset: IAssetTexture) {
  unloadBase(asset);
  if (asset.texture) {
    asset.texture.dispose();
    asset.texture = undefined;
  }
}

export function unloadCubeTexture(asset: IAssetCubeTexture) {
  unloadBase(asset);
  if (asset.cubeTexture) {
    asset.cubeTexture.dispose();
    asset.cubeTexture = undefined;
  }
}

export function unloadVideo(asset: IAssetVideo) {
  unloadBase(asset);
  if (asset.videoTexture) {
    asset.videoTexture.dispose();
    asset.videoTexture.video.src = "";
    asset.videoTexture.video.load();
    asset.videoTexture = undefined;
  }
}

export function unloadAudio(asset: IAssetAudio) {
  unloadBase(asset);
  if (asset.audio) {
    asset.audio.pause();
    asset.audio.dispose();
    asset.audio = undefined;
  }
}
